import React, { useMemo, useState } from "react";
import { Button, message } from "antd";
import { PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import { useDebounce } from "use-debounce";
import dayjs from "dayjs";

import {
  useActiveStoresQuery,
  useActiveChequeStatusesQuery,
  useActiveBankAccountsQuery,
  useChequesQuery,
  useCreateChequeMutation,
  useUpdateChequeMutation,
  useUpdateChequeStatusMutation,
  useDeleteChequeMutation,
  useSectionAccess,
} from "../../Services";
import { PageHeader } from "../../components";
import { PAGE_SIZE, DEBOUNCED_SEARCH_QUERY } from "../../Constant";
import { ChequeAction } from "../../Types";

import { ChequeFilters } from "./common/ChequeFilters";
import {
  ChequeCalendar,
  ChequeFormDrawer,
  ChequeStatsBar,
  ChequeStatusDrawer,
  ChequeTable,
} from "./common";
import {
  ChequeDirection,
  ChequeStatus,
  getChequeDirectionCode,
  getChequeStatusCode,
} from "../../Enums";

export const ChequesPage = () => {
  const [viewMode, setViewMode] = useState<"table" | "calendar">("table");

  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search] = useDebounce(searchInput, DEBOUNCED_SEARCH_QUERY);
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [calendarDate, setCalendarDate] = useState<dayjs.Dayjs>(dayjs());
  const [formDrawerOpen, setFormDrawerOpen] = useState(false);
  const [statusDrawerOpen, setStatusDrawerOpen] = useState(false);
  const [editingCheque, setEditingCheque] = useState<any | null>(null);
  const [updatingCheque, setUpdatingCheque] = useState<any | null>(null);

  const { isAllowed } = useSectionAccess("cheques");

  // Service hooks
  const { data: stores = [] } = useActiveStoresQuery();
  const { data: chequeStatuses = [] } = useActiveChequeStatusesQuery();
  const { data: bankAccounts = [] } = useActiveBankAccountsQuery();

  const {
    data: chequesList,
    isLoading: isListLoading,
    refetch: refetchList,
  } = useChequesQuery(
    {
      page,
      limit: PAGE_SIZE,
      search,
      type: typeFilter,
      status_id: statusFilter,
    },
    { enabled: viewMode === "table" },
  );

  const { data: allChequesList, refetch: refetchAll } = useChequesQuery({
    page: 1,
    limit: 1000,
  });

  const createChequeMutation = useCreateChequeMutation();
  const updateChequeMutation = useUpdateChequeMutation();
  const deleteChequeMutation = useDeleteChequeMutation();
  const updateChequeStatusMutation = useUpdateChequeStatusMutation();

  const refreshData = () => {
    refetchList();
    refetchAll();
  };

  const stats = useMemo(() => {
    let inwardPending = 0;
    let outwardPending = 0;
    let clearedThisMonth = 0;
    let bouncedTotal = 0;
    const currentMonthStr = calendarDate.format("YYYY-MM");

    allChequesList?.forEach((c: any) => {
      const amount = parseFloat(c.amount) || 0;
      if (
        c.type === getChequeDirectionCode(ChequeDirection.IN) &&
        c.status_code === getChequeStatusCode(ChequeStatus.PENDING)
      )
        inwardPending += amount;
      if (
        c.type === getChequeDirectionCode(ChequeDirection.OUT) &&
        c.status_code === getChequeStatusCode(ChequeStatus.PENDING)
      )
        outwardPending += amount;
      if (
        c.status_code === getChequeStatusCode(ChequeStatus.CLEARED) &&
        dayjs(c.cheque_date).format("YYYY-MM") === currentMonthStr
      )
        clearedThisMonth += amount;
      if (c.status_code === getChequeStatusCode(ChequeStatus.BOUNCED))
        bouncedTotal += amount;
    });

    return { inwardPending, outwardPending, clearedThisMonth, bouncedTotal };
  }, [allChequesList, calendarDate]);

  const openAddDrawer = () => {
    setEditingCheque(null);
    setFormDrawerOpen(true);
  };

  const openEditDrawer = (record: any) => {
    setEditingCheque(record);
    setFormDrawerOpen(true);
  };

  const openStatusDrawer = (record: any) => {
    setUpdatingCheque(record);
    setStatusDrawerOpen(true);
  };

  const handleSaveCheque = async (values: any) => {
    try {
      const res = editingCheque
        ? await updateChequeMutation.mutateAsync({
            id: editingCheque.id,
            ...values,
          })
        : await createChequeMutation.mutateAsync(values);

      if (res?.success) {
        message.success(res.message || "Saved.");
        setFormDrawerOpen(false);
        refreshData();
      } else {
        message.error(res?.message || "Failed to save.");
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleSaveStatus = async (values: any) => {
    try {
      const res = await updateChequeStatusMutation.mutateAsync({
        id: updatingCheque.id,
        ...values,
      });
      if (res?.success) {
        message.success(res.message || "Status updated.");
        setStatusDrawerOpen(false);
        refreshData();
      } else {
        message.error(res?.message || "Failed to update status.");
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const res = await deleteChequeMutation.mutateAsync(id);
      if (res?.success) {
        message.success(res.message || "Deleted.");
        refreshData();
      } else {
        message.error(res?.message || "Failed to delete.");
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <PageHeader
        title="Cheque Ledger"
        subText="Track inward and outward cheques."
        handleButtons={[
          ...(isAllowed(ChequeAction.Create) ? [
            {
              title: "Register Cheque",
              icon: PlusOutlined as any,
              onClick: () => openAddDrawer(),
            },
          ] : []),
          {
            title: "",
            icon: ReloadOutlined as any,
            onClick: refreshData,
          },
        ]}
        searchBar={
          <ChequeFilters
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            searchInput={searchInput}
            onSearchChange={(v) => {
              setSearchInput(v);
              setPage(1);
            }}
            typeFilter={typeFilter}
            onTypeChange={(v) => {
              setTypeFilter(v);
              setPage(1);
            }}
            statusFilter={statusFilter}
            onStatusChange={(v) => {
              setStatusFilter(v);
              setPage(1);
            }}
            chequeStatuses={chequeStatuses}
          />
        }
      />

      {/* Stats */}
      <div className="mt-4">
        <ChequeStatsBar
          stats={stats}
          monthLabel={calendarDate.format("MMM YYYY")}
        />
      </div>

      {/* Main content */}
      <div className="mt-4">
        {viewMode === "table" ? (
          <ChequeTable
            data={chequesList}
            total={chequesList?.total || 0}
            page={page}
            pageSize={PAGE_SIZE}
            loading={isListLoading}
            onPageChange={setPage}
            onEdit={openEditDrawer}
            onUpdateStatus={openStatusDrawer}
            onDelete={handleDelete}
          />
        ) : (
          <ChequeCalendar
            calendarDate={calendarDate}
            onDateChange={setCalendarDate}
            cheques={allChequesList}
            onDayClick={openAddDrawer}
            onChequeClick={openStatusDrawer}
          />
        )}
      </div>

      {/* Form Drawer */}
      <ChequeFormDrawer
        open={formDrawerOpen}
        onClose={() => setFormDrawerOpen(false)}
        onSave={handleSaveCheque}
        editingCheque={editingCheque}
        stores={stores}
        chequeStatuses={chequeStatuses}
        loading={
          createChequeMutation.isPending || updateChequeMutation.isPending
        }
      />

      {/* Status Drawer */}
      <ChequeStatusDrawer
        open={statusDrawerOpen}
        onClose={() => setStatusDrawerOpen(false)}
        onSave={handleSaveStatus}
        cheque={updatingCheque}
        chequeStatuses={chequeStatuses}
        bankAccounts={bankAccounts}
      />
    </div>
  );
};
